import {getProductSelection, selectOption} from '@/product/product-selection';
import {getVariantByOption} from '@/product/product-selection/get-variant-by-option';
import {isSizeOption} from '@/product/product-selection/pack-size';
import {ProductOption} from '@/product/types';
import {OptionSettingsMapEntry} from '@/settings/types';
import SelectorIconButton from './SelectorIconButton';

export type VariantSelectorProps = {
  /**
   * The product options settings (should be defined in the Shopify metaobject).
   */
  optionSettings: OptionSettingsMapEntry[];
};

/**
 * A component for selecting product variants.
 * Maps the product options to selector icon buttons.
 * Icons, colors, and descriptions are provided by the option settings.
 * Updates global product selection state when an option is selected.
 * @param optionSettings - The option settings to use for the variant selector.
 * @returns A component for selecting product variants.
 */
export default function VariantSelector() {
  const {product, selectedVariant} = getProductSelection();

  // Ignore the 'Pack Size' option, since this is part of delivery selection,
  // not variant selection.
  const productOptions =
    product?.options.filter(
      (option) => !isSizeOption(option, product.sizeOptionNames)
    ) ?? [];
  const description = selectedVariant?.description;

  /**
   * Handles the click event for a product option.
   *
   * @param option - The product option being clicked.
   * @param value - The selected value for the product option.
   */
  const handleClickOption = (option: ProductOption, value: string) => {
    selectOption({...option, value});
  };

  if (!product) return null;

  return (
    <div className="flex flex-col gap-y-2">
      {productOptions.map((option) => {
        const selectedOptionValue =
          selectedVariant?.selectedOptions.find(
            ({name}) => name === option.name
          )?.value ?? '';

        return (
          <div key={option.name} className="flex flex-col gap-y-2">
            <span>{option.name}:</span>
            <div className="grid grid-cols-2 gap-2">
              {option.values.map((optionValue) => {
                // each button is rendered with the icon and color specified in settings
                const variant = getVariantByOption(
                  product.variants,
                  option.name,
                  optionValue
                );
                const color = variant?.accentColor ?? '';
                const iconUrl = variant?.icon;
                const icon = iconUrl
                  ? {
                      url: iconUrl ?? '',
                      // alt text is ommitted, since this is a decorative icon
                      alt: `${optionValue} icon`,
                    }
                  : undefined;

                return (
                  <SelectorIconButton
                    color={color}
                    dataTestId={optionValue}
                    icon={icon}
                    key={optionValue}
                    onClick={() => handleClickOption(option, optionValue)}
                    selected={optionValue === selectedOptionValue}
                  >
                    {optionValue}
                  </SelectorIconButton>
                );
              })}
            </div>
            <p className="text-sm">{description}</p>
          </div>
        );
      })}
    </div>
  );
}
